import faker from 'faker';
import { ACCOUNT_TYPES } from 'moet-core/src/constants';

/**
 * List of Valid IBAN numbers per country from different sources that BAV accepts
 * Faker does not generate IBANs with valid **account numbers** so this fails on BAV
 * This list should go away once we find or create a better iban generator.
 *
 * Sources:
 * * https://www.iban.com/structure
 * * https://wise.com/sg/iban/example?all=true#structure_and_examples
 *
 */
const VALID_IBAN_PER_COUNTRY = {
  AL: ['AL35202111090000000001234567', 'AL47212110090000000235698741'],
  AD: ['AD1400080001001234567890'],
  AT: ['AT483200000012345864', 'AT342060403102135948'],
  BH: ['BH02CITI00001077181611', 'BH67BMAG00001299123456'],
  BE: ['BE71096123456769', 'BE09967072264457'],
  BA: ['BA393385804800211234'],
  BG: ['BG18RZBB91550123456789', 'BG80BNBG96611020345678', 'BG89RZBB91551482503014'],
  CR: ['CR23015108410026012345', 'CR05015202001026284066'],
  HR: ['HR1723600001101234565', 'HR1210010051863000160'],
  CY: [
    'CY21002001950000357001234567',
    'CY17002001280000001200527600',
    'CY30904000010003000039001000',
  ],
  CZ: ['CZ5508000000001234567899', 'CZ6508000000192000145399', 'CZ6682200006921440349200'],
  DK: ['DK9520000123456789', 'DK8320197788037077', 'DK3479100002256827'],
  EE: ['EE471000001020145685', 'EE382200221020145685', 'EE737700771006453812'],
  FO: ['FO9264600123456789', 'FO6264600001631634'],
  FI: ['FI1410093000123458', 'FI2112345600000785', 'FI0718203000035733'],
  FR: ['FR7630006000011234567890189', 'FR1420041010050500013M02606'],
  GE: ['GE60NB0000000123456789', 'GE29NB0000000101904917'],
  DE: ['DE75512108001245126199', 'DE89370400440532013000', 'DE67672922000001478303'],
  GR: ['GR9608100010000001234567890', 'GR1601101250000000012300695'],
  GL: ['GL8964710123456789', 'GL8964710001000206'],
  HU: [
    'HU93116000060000000012345676',
    'HU42117730161111101800000000',
    'HU10120010080175199500400001',
  ],
  IS: ['IS750001121234563108962099', 'IS140159260076545510730339'],
  IE: ['IE64IRCE92050112345678', 'IE29AIBK93115212345678', 'IE77BARC99021242831001'],
  IL: ['IL170108000000012612345', 'IL620108000000099999999'],
  IT: ['IT60X0542811101000000123456', 'IT60X0542811101000000123456', 'IT20X0306962321100000007197'],
  KW: ['KW81CBKU0000000000001234560101', 'KW81CBKU0000000000001234560101'],
  LV: ['LV97HABA0012345678910'],
  LI: ['LI7408806123456789012', 'LI21088100002324013AA', 'LI0308800000022875748'],
  LT: ['LT601010012345678901', 'LT893200000036487420'],
  LU: ['LU120010001234567891', 'LU280019400644750000', 'LU590019120656249000'],
  MT: [
    'MT31MALT01100000000000000000123',
    'MT84MALT011000012345MTLCAST001S',
    'MT02VALL22013000000040013752732',
  ],
  MC: ['MC5810096180790123456789085'],
  NL: ['NL02ABNA0123456789', 'NL91ABNA0417164300', 'NL79ADYB2017400165'],
  MK: ['MK07200002785123453', 'MK07250120000058984'],
  NO: ['NO8330001234567', 'NO9386011117947', 'NO0239916835985'],
  PL: [
    'PL10105000997603123456789123',
    'PL61109010140000071219812874',
    'PL66102037140000470204007027',
  ],
  PT: ['PT93003506515949621247406', 'PT45003506516911348955865'],
  RO: ['RO09BCYP0000001234567890'],
  SM: ['SM86U0322509800000000270100'],
  SA: ['SA4420000001234567891234', 'SA0380000000608010167519'],
  RS: ['RS35105008123123123173'],
  SK: ['SK8975000000000012345671', 'SK1899520000002107585239'],
  SI: ['SI56192001234567892'],
  ES: ['ES7921000813610123456789', 'ES9121000418450200051332', 'ES0901824370800201633049'],
  SE: ['SE7280000810340009783242', 'SE4550000000058398257466', 'SE9295000099602600817064'],
  CH: ['CH5604835012345678009'],
  UA: ['UA903052992990004149123456789', 'UA213223130000026007233566001'],
  AE: ['AE460090000000123456789', 'AE070331234567890123456'],
  GB: ['GB33BUKB20201555555555', 'GB20BARC20364769542755'],
};

const generateAccountNumber = country => {
  switch (country) {
    case 'US':
    case 'AU':
      return faker.finance.account(faker.datatype.number({ min: 6, max: 10 }));
    case 'CA':
      return faker.finance.account(faker.datatype.number({ min: 7, max: 12 }));
    default:
      if (VALID_IBAN_PER_COUNTRY[country]) {
        return faker.random.arrayElement(VALID_IBAN_PER_COUNTRY[country]);
      }
      return faker.finance.iban(false, country);
  }
};

const generateTransitNumber = country => {
  switch (country) {
    case 'CA':
      return faker.finance.account(faker.datatype.number({ min: 5, max: 5 }));
  }
};

const generateInstitutionNumber = country => {
  switch (country) {
    case 'CA':
      return faker.finance.account(faker.datatype.number({ min: 3, max: 3 }));
  }
};

const generateRoutingNumber = (country, institutionNumber, transitNumber) => {
  switch (country) {
    case 'CA':
      return `0${institutionNumber}${transitNumber}`;
    case 'US':
      return faker.finance.routingNumber();
    case 'AU':
      return faker.finance.account(faker.datatype.number({ min: 6, max: 6 }));
  }
};

const generateAccountType = country => ACCOUNT_TYPES[country] || ACCOUNT_TYPES.IBAN;

function fakeBankAccount(data = {}) {
  const { country, id, currencyCode } = data;
  const accountHolder = `${faker.name.firstName()} ${faker.name.lastName()}`;
  const accountNumber = generateAccountNumber(country);
  const transitNumber = generateTransitNumber(country);
  const institutionNumber = generateInstitutionNumber(country);
  const routingNumber = generateRoutingNumber(country, institutionNumber, transitNumber);

  const canadaPayload = country === 'CA' ? { transitNumber, institutionNumber } : {};

  return {
    id: id,
    type: generateAccountType(country),
    data: {
      accountHolder,
      accountNumber,
      currency: currencyCode,
      ...(routingNumber ? { routingNumber } : {}),
      ...canadaPayload,
      ...data,
    },
    // TODO(cypress-v2): remove these properties below,
    // these is the "old" bankAccount which doesn't match the BE model
    // to avoid refactoring every test on the first iteration,
    // we provide these props here so whatever test that uses the old
    // model can still work
    accountHolder,
    accountNumber,
    currency: currencyCode,
    ...canadaPayload,
    ...data,
  };
}

export function fakeBankAccounts({ country, currencies, defaultAccountCurrency }) {
  const accountDataMap = {
    AUD: {
      currencyCode: 'AUD',
      country: 'AU',
    },
    CAD: {
      currencyCode: 'CAD',
      country: 'CA',
    },
    EUR: {
      currencyCode: 'EUR',
      country: country || 'CH',
    },
    PLN: {
      currencyCode: 'PLN',
      country: 'PL',
    },
    GBP: {
      currencyCode: 'GBP',
      country: 'GB',
    },
    NOK: {
      currencyCode: 'NOK',
      country: 'NO',
    },
    CHF: {
      currencyCode: 'CHF',
      country: 'CH',
    },
    SEK: {
      currencyCode: 'SEK',
      country: 'SE',
    },
    USD: {
      currencyCode: 'USD',
      country: 'US',
    },
  };

  return currencies.map((currency, index) =>
    fakeBankAccount({
      ...(accountDataMap[currency] || accountDataMap.EUR),
      isDefault: currency === defaultAccountCurrency,
      id: 1 + index,
    }),
  );
}

export function bankAccountStepConfig({
  country,
  currencies,
  defaultAccountCurrency = currencies[0],
}) {
  return {
    bankAccounts: {
      value: fakeBankAccounts({
        country,
        currencies,
        defaultAccountCurrency,
      }),
      method: 'spreadArrayProperty',
      path: 'bankAccounts',
    },
  };
}
